import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from "recharts";
import { calculateDailyLimit } from "../../utils/waterConsumption";
import { useUnit } from "../../context/UnitContext";
import { getUnitSymbol } from "../../utils/unitConversion";
import useChartTokens from "../../theme/chartTokens";
import "./WaterBarChart.css";

const ChartTooltip = ({ active, payload, label, unitSymbol, color }) => {
  if (!active || !payload || !payload.length) return null;
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
  return (
    <div className="ng-chart-tooltip">
      <p className="ng-chart-tooltip__label">{label}</p>
      <p className="ng-chart-tooltip__value">
        <span className="ng-chart-tooltip__dot" style={{ background: color }} />
        Consumo: <strong>{Number(total).toFixed(2)} {unitSymbol}</strong>
      </p>
    </div>
  );
};

export default function WaterBarChart({
  householdMembers,
  data = [],
  dailyGoal,
  title = "Consumo diario de agua",
  subtitle,
  showDailyLimit = true,
  height = 260,
  rightSlot,
}) {
  const { selectedUnit, convertValue, formatValueWithUnit } = useUnit();
  const chart = useChartTokens();

  const dailyLimit = dailyGoal !== undefined && dailyGoal !== ''
    ? Number(dailyGoal)
    : calculateDailyLimit(householdMembers);
  const warningLimit = dailyLimit * 0.8;

  const dailyLimitConverted = convertValue(dailyLimit);
  const warningLimitConverted = convertValue(warningLimit);

  const processedData = data.map((d) => {
    const consumoConverted = convertValue(d.consumo);
    return {
      day: d.day,
      Normal: showDailyLimit && consumoConverted > warningLimitConverted ? warningLimitConverted : consumoConverted,
      Exceso: showDailyLimit && consumoConverted > warningLimitConverted ? consumoConverted - warningLimitConverted : 0,
      total: consumoConverted,
    };
  });

  const unitSymbol = getUnitSymbol(selectedUnit);

  return (
    <div className="ng-bar-chart">
      <div className="ng-bar-chart__head">
        <div>
          <h3 className="ng-bar-chart__title">{title}</h3>
          {subtitle ? (
            <p className="ng-bar-chart__subtitle">{subtitle}</p>
          ) : showDailyLimit ? (
            <p className="ng-bar-chart__subtitle">
              Límite diario: {formatValueWithUnit(dailyLimit)}
            </p>
          ) : null}
        </div>
        {rightSlot}
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={processedData}
          margin={{ top: 12, right: 16, left: 0, bottom: 0 }}
          barCategoryGap="22%"
        >
          <CartesianGrid stroke={chart.grid} vertical={false} />
          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={false}
            tick={{ fill: chart.axis, fontSize: 12 }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: chart.axis, fontSize: 12 }}
            width={36}
          />
          <Tooltip
            content={<ChartTooltip unitSymbol={unitSymbol} color={chart.water} />}
            cursor={{ fill: chart.grid, fillOpacity: 0.5 }}
          />
          {showDailyLimit && (
            <ReferenceLine
              y={dailyLimitConverted}
              stroke={chart.goal}
              strokeDasharray="4 4"
            />
          )}
          <Bar dataKey="Normal" stackId="a" fill={chart.water} radius={[3, 3, 0, 0]} maxBarSize={36} />
          <Bar dataKey="Exceso" stackId="a" fill={chart.over} radius={[3, 3, 0, 0]} maxBarSize={36} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
